;(function () {
  class EWGProductComparison extends HTMLElement {
    constructor() {
      super()
      this.expanded = false
      this.handleProductsReady = this.handleProductsReady.bind(this)
      this.handleReportReady = this.handleReportReady.bind(this)
      this.handleUnavailable = this.handleUnavailable.bind(this)
      this.handleToggle = this.handleToggle.bind(this)
    }

    connectedCallback() {
      this.scroll = this.querySelector('[data-comparison-scroll]')
      this.loading = this.querySelector('[data-comparison-loading]')
      this.table = this.querySelector('[data-comparison-table]')
      this.productsRow = this.querySelector('[data-comparison-products]')
      this.body = this.querySelector('[data-comparison-body]')
      this.productTemplate = this.querySelector('[data-comparison-product-template]')
      this.valueTemplate = this.querySelector('[data-comparison-value-template]')
      this.toggleWrapper = this.querySelector('[data-comparison-toggle-wrapper]')
      this.toggleButton = this.querySelector('.ewg-product-comparison__toggle')
      this.fields = Array.from(this.querySelectorAll('[data-comparison-field]'))
        .map(field => ({
          key: field.dataset.fieldKey?.trim(),
          label: field.dataset.fieldLabel?.trim()
        }))
        .filter(field => field.key && field.label)

      this.fallbackProducts = this.readFallbackProducts()
      this.toggleButton.type = 'button'
      this.toggleButton.setAttribute('aria-controls', this.scroll.id)
      this.toggleButton.setAttribute('aria-expanded', 'false')
      this.toggleButton.addEventListener('click', this.handleToggle)
      document.addEventListener('wd:ewg-products-ready', this.handleProductsReady)
      document.addEventListener('wd:ewg-ready', this.handleReportReady)
      document.addEventListener('wd:ewg-empty', this.handleUnavailable)
      document.addEventListener('wd:ewg-error', this.handleUnavailable)

      if (Array.isArray(window.WDEwgRecommendedProducts) && window.WDEwgRecommendedProducts.length) {
        this.renderProducts(window.WDEwgRecommendedProducts)
      }

      const store = window.WDEwgReportStore
      if (typeof store?.init === 'function') {
        store.init().then(state => this.applyProducts(state))
        return
      }
      this.handleUnavailable()
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener('click', this.handleToggle)
      document.removeEventListener('wd:ewg-products-ready', this.handleProductsReady)
      document.removeEventListener('wd:ewg-ready', this.handleReportReady)
      document.removeEventListener('wd:ewg-empty', this.handleUnavailable)
      document.removeEventListener('wd:ewg-error', this.handleUnavailable)
    }

    readFallbackProducts() {
      const node = this.querySelector('[data-fallback-products]')
      if (!node) return []
      try {
        const parsed = JSON.parse(node.textContent)
        return Array.isArray(parsed) ? parsed.filter(Boolean) : []
      } catch {
        return []
      }
    }

    applyProducts(state) {
      const products = state?.recommendedProducts || state?.information?.recommendedProducts
      if (Array.isArray(products) && products.length) {
        this.renderProducts(products)
        return
      }
      this.handleUnavailable()
    }

    handleProductsReady(event) {
      const products = event.detail?.products
      if (Array.isArray(products) && products.length) this.renderProducts(products)
    }

    handleReportReady(event) {
      this.applyProducts(event.detail)
    }

    handleUnavailable() {
      if (Array.isArray(window.WDEwgRecommendedProducts) && window.WDEwgRecommendedProducts.length) {
        this.renderProducts(window.WDEwgRecommendedProducts)
        return
      }
      if (this.fallbackProducts.length) this.renderProducts(this.fallbackProducts)
    }

    handleToggle() {
      this.expanded = !this.expanded
      this.querySelectorAll('[data-extra-row]').forEach(row => {
        row.hidden = !this.expanded
      })
      this.toggleButton.setAttribute('aria-expanded', String(this.expanded))
      this.toggleButton.setAttribute(
        'aria-label',
        `${this.expanded ? this.toggleWrapper.dataset.lessLabel : this.toggleWrapper.dataset.moreLabel} product comparison`
      )

      const labelNode = Array.from(this.toggleButton.childNodes).find(
        node => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
      )
      if (labelNode) {
        labelNode.nodeValue = ` ${
          this.expanded ? this.toggleWrapper.dataset.lessLabel : this.toggleWrapper.dataset.moreLabel
        } `
      }

      if (this.expanded) {
        this.collapseScrollY = window.scrollY
        return
      }

      const nav = document.querySelector('.ewg-sticky-nav.is-visible')
      const specs = this.body || this
      window.scrollTo({
        top:
          this.collapseScrollY != null
            ? this.collapseScrollY
            : specs.getBoundingClientRect().top + window.scrollY - (nav?.offsetHeight || 0),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      })
    }

    renderProducts(products) {
      if (
        !this.scroll ||
        !this.table ||
        !this.productsRow ||
        !this.body ||
        !this.productTemplate ||
        !this.valueTemplate
      ) {
        return
      }

      const visibleProducts = products.slice(0, 3)
      if (!visibleProducts.length) return

      // 数据未变化时跳过重渲染，避免重建表格与重复下载图片
      const renderKey = JSON.stringify(visibleProducts)
      if (renderKey === this.renderedKey) return
      this.renderedKey = renderKey

      this.productsRow.querySelectorAll('.ewg-product-comparison__product-cell').forEach(cell => cell.remove())
      this.body.replaceChildren()
      this.table.style.setProperty('--comparison-products', visibleProducts.length)
      this.expanded = false

      const priceFormatters = {}
      const productCellsFragment = document.createDocumentFragment()

      visibleProducts.forEach((product, index) => {
        const productFragment = this.productTemplate.content.cloneNode(true)
        const productCell = productFragment.querySelector('.ewg-product-comparison__product-cell')
        const productTitle = product.product_title || product.title || ''
        const productUrl = this.isSafeHref(product.product_url || product.url) ? (product.product_url || product.url).trim() : '#'
        const productImage = product.product_image || product.image || product.imageUrl || product.featuredImage || ''
        const currencyCode = product.currency_code || 'USD'
        const priceFormatter =
          priceFormatters[currencyCode] ||
          (priceFormatters[currencyCode] = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }))
        const discountPrice = product.discount_price ?? product.price ?? ''
        const originalPrice = product.original_price ?? product.compareAtPrice ?? product.compare_at_price ?? ''
        const discountPriceNumber = Number(discountPrice)
        const originalPriceNumber = Number(originalPrice)
        const displayPrice =
          discountPrice !== '' && Number.isFinite(discountPriceNumber)
            ? priceFormatter.format(discountPriceNumber)
            : discountPrice
        const hasDiscount =
          Number.isFinite(originalPriceNumber) &&
          Number.isFinite(discountPriceNumber) &&
          originalPriceNumber > discountPriceNumber
        const displayOriginalPrice = hasDiscount ? priceFormatter.format(originalPriceNumber) : ''
        const savingValue = product.saving_display || product.saved_price || ''
        const savingNumber = Number(savingValue)
        const displaySaving =
          hasDiscount && savingValue !== '' && Number.isFinite(savingNumber) && savingNumber > 0
            ? priceFormatter.format(savingNumber).replace(/\.00$/, '')
            : ''

        if (index % 2 === 0) productCell.classList.add('ewg-product-comparison__product-cell--shaded')

        const productLink = productFragment.querySelector('[data-product-link]')
        productLink.href = productUrl
        productLink.setAttribute('aria-label', `View ${productTitle || 'recommended product'}`)

        const image = productFragment.querySelector('[data-product-image]')
        // Shopify CDN 图片按展示尺寸请求（120px @2x），避免下载原图
        image.src =
          typeof productImage === 'string' && productImage.includes('cdn.shopify.com')
            ? `${productImage}${productImage.includes('?') ? '&' : '?'}width=240`
            : productImage
        image.alt = productTitle

        productFragment.querySelector('[data-product-title]').textContent = productTitle
        productFragment.querySelector('[data-product-price]').textContent = displayPrice

        const comparePrice = productFragment.querySelector('[data-product-compare-price]')
        comparePrice.textContent = displayOriginalPrice
        comparePrice.hidden = !displayOriginalPrice
        const saving = productFragment.querySelector('[data-product-saving]')
        saving.textContent = displaySaving ? `${displaySaving} Off` : ''
        saving.hidden = !displaySaving

        const addToCart = productFragment.querySelector('[data-add-to-cart]')
        const variantId = String(product.variant_id || product.variantId || '')
          .split('/')
          .pop()
        const available = product.available_for_sale ?? product.available ?? true
        const inventoryQuantity = product.inventory_quantity ?? product.inventoryQuantity
        addToCart.dataset.atcId = variantId
        addToCart.dataset.atcAvailable = String(available)
        addToCart.dataset.atcInventory =
          inventoryQuantity === null || inventoryQuantity === undefined ? '' : String(inventoryQuantity)
        addToCart.dataset.atcInventoryPolicy = product.inventory_policy || product.inventoryPolicy || ''
        addToCart.dataset.atcInventoryManagement = product.inventory_management || product.inventoryManagement || ''
        addToCart.dataset.eventSku = product.sku || ''
        addToCart.setAttribute('aria-label', `Add to Cart ${productTitle}`)

        if (!variantId || !available) {
          addToCart.disabled = true
          addToCart.ariaDisabled = 'true'
          addToCart.querySelector('.component-atc-button__text').textContent = 'Sold Out'
        }

        productCellsFragment.appendChild(productFragment)
      })

      this.productsRow.appendChild(productCellsFragment)

      let renderedFieldCount = 0
      const rowsFragment = document.createDocumentFragment()
      const valueParser = document.createElement('template')
      this.fields.forEach(field => {
        const fieldValues = visibleProducts.map(product => {
          const specs = Array.isArray(product.specs) ? product.specs : []
          const productValue = product[field.key]
          return productValue === null || productValue === undefined || productValue === ''
            ? specs.find(spec => spec.label === field.key)?.value
            : productValue
        })
        const hasValue = fieldValues.some(value => {
          if (value === null || value === undefined || value === '') return false
          if (typeof value !== 'string') return true
          if (!/<[^>]+>/.test(value)) return value.trim() !== ''
          valueParser.innerHTML = this.sanitizeRichText(value)
          return valueParser.content.textContent.trim() !== ''
        })
        if (!hasValue) return

        const mobileLabelRow = document.createElement('tr')
        mobileLabelRow.className = 'ewg-product-comparison__mobile-label-row'
        mobileLabelRow.innerHTML = `
          <td class="ewg-product-comparison__mobile-label-cell" colspan="${visibleProducts.length}">
            <span class="ewg-product-comparison__mobile-label"></span>
          </td>
        `
        mobileLabelRow.querySelector('.ewg-product-comparison__mobile-label').textContent = field.label

        const valueRow = document.createElement('tr')
        valueRow.className = 'ewg-product-comparison__value-row'

        const labelCell = document.createElement('th')
        labelCell.className = 'ewg-product-comparison__label-cell'
        labelCell.scope = 'row'
        labelCell.textContent = field.label
        valueRow.appendChild(labelCell)

        visibleProducts.forEach((product, productIndex) => {
          const valueFragment = this.valueTemplate.content.cloneNode(true)
          const valueCell = valueFragment.querySelector('.ewg-product-comparison__value-cell')
          const value = fieldValues[productIndex]

          if (productIndex % 2 === 0) valueCell.classList.add('ewg-product-comparison__value-cell--shaded')
          const valueElement = valueFragment.querySelector('[data-comparison-value]')
          if (value === null || value === undefined || value === '') {
            valueElement.textContent = '—'
          } else if (typeof value === 'string' && /<[^>]+>/.test(value)) {
            valueElement.innerHTML = this.sanitizeRichText(value)
          } else {
            valueElement.textContent = String(value)
          }
          valueRow.appendChild(valueFragment)
        })

        if (renderedFieldCount >= 8) {
          mobileLabelRow.dataset.extraRow = ''
          valueRow.dataset.extraRow = ''
          mobileLabelRow.hidden = true
          valueRow.hidden = true
        }

        rowsFragment.appendChild(mobileLabelRow)
        rowsFragment.appendChild(valueRow)
        renderedFieldCount += 1
      })

      this.body.appendChild(rowsFragment)

      this.toggleWrapper.hidden = renderedFieldCount <= 8
      this.toggleButton.setAttribute('aria-expanded', 'false')
      const toggleLabelNode = Array.from(this.toggleButton.childNodes).find(
        node => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
      )
      if (toggleLabelNode) toggleLabelNode.nodeValue = ` ${this.toggleWrapper.dataset.moreLabel} `

      this.loading.hidden = true
      this.table.hidden = false
      this.scroll.classList.add('is-loaded')
      this.scroll.ariaBusy = 'false'
    }

    isSafeHref(url) {
      if (typeof url !== 'string' || !url.trim()) return false
      const value = url.trim()
      if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true
      try {
        const parsed = new URL(value, window.location.origin)
        return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.origin === window.location.origin
      } catch {
        return false
      }
    }

    isSafeRichTextHref(url) {
      if (typeof url !== 'string' || !url.trim()) return false
      const value = url.trim()
      if (value.startsWith('#') && !value.includes(':')) return true
      if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true
      try {
        const parsed = new URL(value, window.location.origin)
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false
        if (parsed.origin === window.location.origin) return true
        return parsed.protocol === 'https:' && parsed.hostname === 'cdn.shopify.com'
      } catch {
        return false
      }
    }

    sanitizeRichText(html) {
      if (typeof html !== 'string' || !html.trim()) return ''
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const allowedTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'A', 'SPAN', 'DIV'])

      const clean = node => {
        ;[...node.childNodes].forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) return
          if (child.nodeType !== Node.ELEMENT_NODE) {
            child.remove()
            return
          }
          if (!allowedTags.has(child.tagName)) {
            const text = doc.createTextNode(child.textContent || '')
            child.replaceWith(text)
            return
          }
          ;[...child.attributes].forEach(attr => {
            const name = attr.name.toLowerCase()
            if (child.tagName === 'A' && name === 'href' && this.isSafeRichTextHref(attr.value)) {
              child.setAttribute('href', attr.value.trim())
              child.setAttribute('rel', 'noopener noreferrer')
              return
            }
            if (child.tagName === 'A' && name === 'target' && attr.value === '_blank') return
            if (name === 'class' && /^[a-z0-9\s_-]{1,80}$/i.test(attr.value)) return
            child.removeAttribute(attr.name)
          })
          if (child.tagName === 'A' && !child.getAttribute('href')) {
            const text = doc.createTextNode(child.textContent || '')
            child.replaceWith(text)
            return
          }
          clean(child)
        })
      }

      clean(doc.body)
      return doc.body.innerHTML
    }
  }

  if (!customElements.get('ewg-product-comparison')) {
    customElements.define('ewg-product-comparison', EWGProductComparison)
  }
})()
